package com.bulut.mecitogullari.ui.components

import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.OffsetMapping
import androidx.compose.ui.text.input.TransformedText
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp

/**
 * Lira amount input: state holds plain digits ("4000000"), the field
 * displays them grouped ("4.000.000"). Whole liras only — no kuruş.
 */
@Composable
fun AmountField(
    label: String,
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
) {
    OutlinedTextField(
        value = value,
        onValueChange = { input ->
            onValueChange(input.filter(Char::isDigit).take(12).trimStart('0'))
        },
        label = { Text(label) },
        singleLine = true,
        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
        visualTransformation = remember { ThousandsSeparatorTransformation() },
        shape = RoundedCornerShape(12.dp),
        modifier = modifier,
    )
}

/**
 * Kuruş-capable amount input: state holds "154500,5" style raw text,
 * the field displays "154.500,5". Dot key is accepted as a comma.
 */
@Composable
fun DecimalAmountField(
    label: String,
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
) {
    OutlinedTextField(
        value = value,
        onValueChange = { input -> onValueChange(sanitizeDecimal(input)) },
        label = { Text(label) },
        singleLine = true,
        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
        visualTransformation = remember { DecimalThousandsTransformation() },
        shape = RoundedCornerShape(12.dp),
        modifier = modifier,
    )
}

/** Digits + a single comma, max 2 decimals, no leading zeros. */
private fun sanitizeDecimal(input: String): String {
    val sb = StringBuilder()
    var hasComma = false
    var decimals = 0
    for (c in input) {
        when {
            c.isDigit() && !hasComma -> if (sb.count { it.isDigit() } < 12) sb.append(c)
            c.isDigit() && decimals < 2 -> {
                sb.append(c)
                decimals++
            }
            (c == ',' || c == '.') && !hasComma -> {
                hasComma = true
                sb.append(',')
            }
        }
    }
    var intPart = sb.toString().substringBefore(',').trimStart('0')
    val decPart = if (hasComma) "," + sb.toString().substringAfter(',') else ""
    if (intPart.isEmpty() && hasComma) intPart = "0"
    return intPart + decPart
}

/** Groups the integer part with '.', leaves ",kuruş" untouched. */
private class DecimalThousandsTransformation : VisualTransformation {
    override fun filter(text: AnnotatedString): TransformedText {
        val raw = text.text
        if (raw.isEmpty()) return TransformedText(text, OffsetMapping.Identity)

        val commaIndex = raw.indexOf(',')
        val intPart = if (commaIndex >= 0) raw.take(commaIndex) else raw
        val decPart = if (commaIndex >= 0) raw.substring(commaIndex) else ""
        val groupedInt = buildString {
            intPart.forEachIndexed { index, char ->
                if (index > 0 && (intPart.length - index) % 3 == 0) append('.')
                append(char)
            }
        }
        val out = groupedInt + decPart

        val mapping = object : OffsetMapping {
            override fun originalToTransformed(offset: Int): Int {
                val clamped = offset.coerceIn(0, raw.length)
                val withinInt = clamped.coerceAtMost(intPart.length)
                var separators = 0
                for (i in 1 until withinInt) {
                    if ((intPart.length - i) % 3 == 0) separators++
                }
                // Positions past the integer part shift by every separator.
                if (clamped >= intPart.length && intPart.isNotEmpty()) {
                    separators = (intPart.length - 1) / 3
                }
                return (clamped + separators).coerceIn(0, out.length)
            }

            override fun transformedToOriginal(offset: Int): Int {
                val clamped = offset.coerceIn(0, out.length)
                val dots = out.take(clamped).count { it == '.' }
                return (clamped - dots).coerceIn(0, raw.length)
            }
        }
        return TransformedText(AnnotatedString(out), mapping)
    }
}

/** Inserts "." between thousands groups while keeping cursor mapping exact. */
private class ThousandsSeparatorTransformation : VisualTransformation {

    override fun filter(text: AnnotatedString): TransformedText {
        val digits = text.text
        if (digits.isEmpty()) return TransformedText(text, OffsetMapping.Identity)

        val grouped = buildString {
            digits.forEachIndexed { index, char ->
                if (index > 0 && (digits.length - index) % 3 == 0) append('.')
                append(char)
            }
        }

        val mapping = object : OffsetMapping {
            override fun originalToTransformed(offset: Int): Int {
                var separators = 0
                for (i in 1 until offset.coerceIn(0, digits.length)) {
                    if ((digits.length - i) % 3 == 0) separators++
                }
                return (offset + separators).coerceIn(0, grouped.length)
            }

            override fun transformedToOriginal(offset: Int): Int {
                val clamped = offset.coerceIn(0, grouped.length)
                val separators = grouped.take(clamped).count { it == '.' }
                return (clamped - separators).coerceIn(0, digits.length)
            }
        }

        return TransformedText(AnnotatedString(grouped), mapping)
    }
}
